import { formatTime24 } from './dateTime';

export function openNativePicker(e) {
  const el = e?.currentTarget;
  if (el && typeof el.showPicker === 'function') {
    try {
      el.showPicker();
    } catch {
      // ignore
    }
  }
}

export function cityKeyFromText(text) {
  const s = String(text || '').trim();
  if (!s) return '';
  const head = s.split(',', 1)[0].trim().toLowerCase();
  if (!head) return '';
  const stripped = head
    .replace(/\b(international\s+)?airport\b/g, '')
    .replace(/\btrain\s+station\b/g, '')
    .replace(/\brailway\s+station\b/g, '')
    .replace(/\bstation\b/g, '')
    .replace(/\bterminal\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped || head;
}

export function addDays(dateStr, days) {
  const s = String(dateStr || '').trim();
  const d = Number(days) || 0;
  if (!s) return '';
  try {
    const base = new Date(`${s}T00:00:00Z`);
    if (Number.isNaN(base.getTime())) return s;
    base.setUTCDate(base.getUTCDate() + d);
    return base.toISOString().slice(0, 10);
  } catch {
    return s;
  }
}

export function diffDays(fromDateStr, toDateStr) {
  const a = String(fromDateStr || '').trim();
  const b = String(toDateStr || '').trim();
  if (!a || !b) return 0;
  try {
    const da = new Date(`${a}T00:00:00Z`);
    const db = new Date(`${b}T00:00:00Z`);
    if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return 0;
    return Math.round((db.getTime() - da.getTime()) / 86400000);
  } catch {
    return 0;
  }
}

export function toMinutes(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return null;
  const m = hhmm.match(/^(\d{2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mm)) return null;
  return h * 60 + mm;
}

export function formatDurationMinutes(mins) {
  const n = Number(mins);
  if (!Number.isFinite(n)) return '';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(Math.round(n));
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h && m) return `${sign}${h}h ${m}m`;
  if (h) return `${sign}${h}h`;
  return `${sign}${m}m`;
}

export function toIso(dateStr, hhmm) {
  const dd = String(dateStr || '').trim();
  const v = String(hhmm || '').trim();
  if (!dd || !v) return null;
  return `${dd}T${v}`;
}

export function buildLegsPayload({ useLegs, legsDraft, typeId, fallbackDate }) {
  if (!useLegs) return null;
  const list = Array.isArray(legsDraft) ? legsDraft : [];
  const date = String(fallbackDate || '').trim();

  return list
    .map((l, idx) => {
      const from_address = String(l?.from_address || '').trim();
      const to_address = String(l?.to_address || '').trim();
      const depart_date = String(l?.depart_date || date || '').trim();
      const arrive_day_offset = Number(l?.arrive_day_offset) || 0;
      const arrive_date = addDays(depart_date, Math.max(0, Math.min(2, arrive_day_offset)));
      return {
        leg_index: idx,
        type_id: String(l?.type_id || typeId || '').trim() || null,
        airline: String(l?.airline || '').trim() || null,
        from_address: from_address || null,
        from_lat: Number.isFinite(Number(l?.from_lat)) ? Number(l.from_lat) : null,
        from_lon: Number.isFinite(Number(l?.from_lon)) ? Number(l.from_lon) : null,
        from_city: String(l?.from_city || '').trim() || null,
        to_address: to_address || null,
        to_lat: Number.isFinite(Number(l?.to_lat)) ? Number(l.to_lat) : null,
        to_lon: Number.isFinite(Number(l?.to_lon)) ? Number(l.to_lon) : null,
        to_city: String(l?.to_city || '').trim() || null,
        depart_time: toIso(depart_date, l?.depart_time),
        arrive_time: toIso(arrive_date, l?.arrive_time),
      };
    })
    .filter(l => l.from_address && l.to_address);
}

export function deriveStartEnd({ useLegs, legsPayload, dateStr, startTime, endTime, endDateStr }) {
  const date = String(dateStr || '').trim();
  const endDate = String(endDateStr || dateStr || '').trim();
  if (useLegs && Array.isArray(legsPayload) && legsPayload.length) {
    const startIso = legsPayload[0].depart_time || (date ? `${date}T${(startTime || '00:00')}` : null);
    const endIso = legsPayload[legsPayload.length - 1].arrive_time || (endDate && endTime ? `${endDate}T${endTime}` : null);
    return { startIso, endIso };
  }

  const startIso = date ? `${date}T${(startTime || '00:00')}` : null;
  const endIso = endDate && endTime ? `${endDate}T${endTime}` : null;
  return { startIso, endIso };
}

export function hydrateLegDraftsFromActivity(activity, fallbackDate) {
  const legs = Array.isArray(activity?.legs) ? activity.legs : [];
  const ordered = [...legs].sort((a, b) => (Number(a?.leg_index) || 0) - (Number(b?.leg_index) || 0));
  const date = String(fallbackDate || '').slice(0, 10);

  return ordered.map((l, idx) => {
    const dep = String(l?.depart_local_date || date || '').slice(0, 10);
    const arr = String(l?.arrive_local_date || '').slice(0, 10);
    const d = diffDays(dep, arr);
    const arrive_day_offset = d <= 0 ? 0 : (d >= 2 ? 2 : d);

    const fromTz = l?.from_tz || 'UTC';
    const toTz = l?.to_tz || 'UTC';
    return {
      leg_index: Number.isFinite(Number(l?.leg_index)) ? Number(l.leg_index) : idx,
      type_id: String(l?.type_id || activity?.type_id || ''),
      airline: l?.airline || '',
      from_address: l?.from_address || '',
      from_lat: l?.from_lat ?? null,
      from_lon: l?.from_lon ?? null,
      from_city: l?.from_city || '',
      to_address: l?.to_address || '',
      to_lat: l?.to_lat ?? null,
      to_lon: l?.to_lon ?? null,
      to_city: l?.to_city || '',
      depart_date: dep,
      depart_time: formatTime24(l?.depart_time, fromTz) || '',
      arrive_day_offset,
      arrive_time: formatTime24(l?.arrive_time, toTz) || '',
    };
  });
}
